import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, RefreshCw, Send, Facebook, Instagram, MessageCircle, Linkedin,
  CheckCircle2, FlaskConical, XCircle, Save, ShieldCheck, RotateCcw, Trash2,
  BarChart3, Trophy, Music2, Stethoscope,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import TelegramLinkedInSetup from './TelegramLinkedInSetup';
import TikTokSetup from './TikTokSetup';
import DistributionDiagnosticModal from './DistributionDiagnosticModal';
import MetaConnect from './MetaConnect';

const CHANNEL_META = {
  facebook: { label: 'Facebook Page', Icon: Facebook, color: 'text-blue-600' },
  instagram: { label: 'Instagram', Icon: Instagram, color: 'text-pink-600' },
  linkedin: { label: 'LinkedIn', Icon: Linkedin, color: 'text-sky-700' },
  telegram: { label: 'Telegram', Icon: Send, color: 'text-cyan-600' },
  tiktok: { label: 'TikTok', Icon: Music2, color: 'text-black' },
  whatsapp: { label: 'WhatsApp', Icon: MessageCircle, color: 'text-green-600' },
};

const STATUS_META = {
  success: { label: 'Publicado', Icon: CheckCircle2, cls: 'bg-green-100 text-green-700' },
  simulated: { label: 'Simulado', Icon: FlaskConical, cls: 'bg-amber-100 text-amber-700' },
  manual: { label: 'Manual', Icon: Send, cls: 'bg-sky-100 text-sky-700' },
  processing: { label: 'Procesando', Icon: FlaskConical, cls: 'bg-orange-100 text-orange-700' },
  test: { label: 'Prueba', Icon: FlaskConical, cls: 'bg-violet-100 text-violet-700' },
  failed: { label: 'Falló', Icon: XCircle, cls: 'bg-red-100 text-red-700' },
};

function StatCard({ label, value, Icon, cls }) {
  return (
    <div className="bg-white rounded-2xl p-4 flex items-center gap-3 border border-gray-100" data-testid={`dist-stat-${label}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cls}`}><Icon className="w-5 h-5" /></div>
      <div>
        <p className="text-2xl font-bold text-habita-dark leading-none">{value}</p>
        <p className="text-xs text-gray-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

function CredInput({ label, name, form, setForm, placeholder, isSet }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
        {label}
        {isSet && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">guardado</span>}
      </label>
      <input
        data-testid={`dist-cred-${name}`}
        value={form[name] || ''}
        onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
        placeholder={isSet ? '•••••••• (dejar vacío para no cambiar)' : placeholder}
        className="w-full mt-1 px-3 h-10 rounded-xl border border-gray-200 outline-none focus:border-habita text-sm bg-white"
      />
    </div>
  );
}

export default function DistributionPanel() {
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [ab, setAb] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState({ channel: '', status: '', trigger: '' });
  const [form, setForm] = useState({});
  const [diagLog, setDiagLog] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, st, lg, ab] = await Promise.all([
        api.distributionSettings(),
        api.distributionStats(),
        api.distributionLogs(filter),
        api.distributionAbStats(),
      ]);
      setSettings(s);
      setStats(st);
      setLogs(lg.items || []);
      setAb(ab);
    } catch (e) {
      toast({ title: 'Error al cargar', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filter, toast]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async (patch) => {
    setSaving(true);
    try {
      const meta = {
        app_id: form.app_id, app_secret: form.app_secret, page_id: form.page_id,
        page_access_token: form.page_access_token, ig_user_id: form.ig_user_id,
      };
      const telegram = { bot_token: form.tg_bot_token, chat_id: form.tg_chat_id };
      const linkedin = { client_id: form.li_client_id, client_secret: form.li_client_secret };
      const res = await api.saveDistributionSettings({ meta, telegram, linkedin, ...patch });
      setSettings(res);
      setForm({});
      toast({ title: 'Configuración guardada', description: 'Los cambios se aplicaron correctamente.' });
    } catch (e) {
      toast({ title: 'Error al guardar', description: String(e.message || e), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const toggleChannel = (ch) => {
    if (!settings) return;
    const channels = { ...settings.channels, [ch]: !settings.channels[ch] };
    saveConfig({ channels });
  };

  const toggleDryRun = () => settings && saveConfig({ dry_run: !settings.dry_run });

  const retry = async (id) => {
    try {
      await api.retryDistribution(id);
      toast({ title: 'Reintento ejecutado' });
      load();
    } catch (e) {
      toast({ title: 'Error en reintento', description: String(e.message || e), variant: 'destructive' });
    }
  };

  const clearCreds = async () => {
    if (!window.confirm('¿Borrar todas las credenciales de Meta/WhatsApp? El motor vuelve a modo simulado.')) return;
    try {
      const res = await api.clearDistributionCredentials();
      setSettings(res);
      setForm({});
      toast({ title: 'Credenciales borradas', description: 'El motor volvió a modo simulado (dry-run).' });
    } catch (e) {
      toast({ title: 'Error al borrar', description: String(e.message || e), variant: 'destructive' });
    }
  };

  if (loading) return (
    <div className="bg-white rounded-2xl p-12 text-center" data-testid="dist-panel-loading">
      <Loader2 className="w-8 h-8 text-habita animate-spin mx-auto" />
    </div>
  );

  const m = settings?.meta || {};

  return (
    <div className="space-y-6" data-testid="distribution-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-habita-dark">Distribution Center</h2>
          <p className="text-sm text-gray-500">Publicación automática al aprobar un inmueble (Meta + WhatsApp).</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="dist-dryrun-toggle"
            onClick={toggleDryRun}
            disabled={saving}
            className={`px-3 h-9 rounded-full text-xs font-bold border-2 flex items-center gap-1.5 transition-colors ${
              settings?.dry_run ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-green-500 bg-green-50 text-green-700'
            }`}
          >
            {settings?.dry_run ? <><FlaskConical className="w-3.5 h-3.5" /> Modo simulado (dry-run)</> : <><ShieldCheck className="w-3.5 h-3.5" /> Publicando EN VIVO</>}
          </button>
          <button onClick={load} className="w-9 h-9 rounded-full border border-gray-300 hover:bg-habita-cream flex items-center justify-center" data-testid="dist-refresh-btn">
            <RefreshCw className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={stats?.total || 0} Icon={Send} cls="bg-habita/10 text-habita" />
        <StatCard label="Publicados" value={stats?.by_status?.success || 0} Icon={CheckCircle2} cls="bg-green-100 text-green-700" />
        <StatCard label="Simulados" value={stats?.by_status?.simulated || 0} Icon={FlaskConical} cls="bg-amber-100 text-amber-700" />
        <StatCard label="Fallidos" value={stats?.by_status?.failed || 0} Icon={XCircle} cls="bg-red-100 text-red-700" />
      </div>

      {/* Meta OAuth profesional */}
      <MetaConnect settings={settings} onReload={load} />

      {/* Canales + credenciales */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100 space-y-5">
        <h3 className="font-bold text-habita-dark">Canales y credenciales</h3>

        {/* toggles de canal */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(CHANNEL_META).map(([ch, { label, Icon, color }]) => {
            const on = settings?.channels?.[ch];
            const ready = ch === 'facebook' ? m.facebook_live_ready
              : ch === 'instagram' ? m.instagram_live_ready
              : ch === 'telegram' ? settings?.telegram?.telegram_live_ready
              : ch === 'linkedin' ? settings?.linkedin?.linkedin_live_ready
              : settings?.whatsapp?.configured;
            return (
              <button
                key={ch}
                data-testid={`dist-channel-toggle-${ch}`}
                onClick={() => toggleChannel(ch)}
                className={`px-3 h-10 rounded-xl border-2 text-sm font-semibold flex items-center gap-2 ${on ? 'border-habita bg-habita/5' : 'border-gray-200 opacity-60'}`}
              >
                <Icon className={`w-4 h-4 ${color}`} /> {label}
                <span className={`w-2 h-2 rounded-full ${ready ? 'bg-green-500' : 'bg-gray-300'}`} title={ready ? 'Listo para publicar en vivo' : 'Sin credenciales (simulado)'} />
              </button>
            );
          })}
        </div>

        {/* Meta creds (fallback manual — el método principal es OAuth arriba) */}
        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 mb-2">Credenciales Meta manuales (respaldo administrativo)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CredInput label="Meta App ID" name="app_id" form={form} setForm={setForm} placeholder="1234567890" isSet={m.app_id_set} />
            <CredInput label="Meta App Secret" name="app_secret" form={form} setForm={setForm} placeholder="app secret" isSet={m.app_secret_set} />
            <CredInput label="Facebook Page ID" name="page_id" form={form} setForm={setForm} placeholder="page id" isSet={m.page_id_set} />
            <CredInput label="Page Access Token (larga duración)" name="page_access_token" form={form} setForm={setForm} placeholder="EAAB..." isSet={m.page_access_token_set} />
            <CredInput label="Instagram Business Account ID" name="ig_user_id" form={form} setForm={setForm} placeholder="1784..." isSet={m.ig_user_id_set} />
          </div>
        </div>

        {/* Telegram + LinkedIn creds */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-gray-100">
          <CredInput label="Telegram Bot Token" name="tg_bot_token" form={form} setForm={setForm} placeholder="123456:ABC-DEF..." isSet={settings?.telegram?.bot_token_set} />
          <CredInput label="Telegram Chat/Canal ID" name="tg_chat_id" form={form} setForm={setForm} placeholder="@mi_canal o -100123..." isSet={settings?.telegram?.chat_id_set} />
          <CredInput label="LinkedIn Client ID" name="li_client_id" form={form} setForm={setForm} placeholder="86xxxxxxxxxxxx" isSet={settings?.linkedin?.client_id_set} />
          <CredInput label="LinkedIn Client Secret" name="li_client_secret" form={form} setForm={setForm} placeholder="WPL_AP1..." isSet={settings?.linkedin?.client_secret_set} />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-400">LinkedIn: guarda Client ID + Secret y luego conéctalo por OAuth abajo (Access Token y página se obtienen automáticamente). WhatsApp: scaffold. Telegram publica en vivo con Bot Token + Chat ID.</p>
          <div className="flex items-center gap-2">
            <button
              data-testid="dist-clear-creds-btn"
              onClick={clearCreds}
              disabled={saving}
              className="px-4 h-10 rounded-xl border border-rose-200 text-rose-600 font-semibold text-sm flex items-center gap-2 hover:bg-rose-50 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" /> Borrar credenciales
            </button>
            <button
              data-testid="dist-save-creds-btn"
              onClick={() => saveConfig({})}
              disabled={saving}
              className="px-4 h-10 rounded-xl bg-habita text-white font-semibold text-sm flex items-center gap-2 hover:bg-habita-dark disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar credenciales
            </button>
          </div>
        </div>
      </div>

      {/* Guías + diagnóstico de Telegram y LinkedIn */}
      <TelegramLinkedInSetup settings={settings} onReload={load} />

      {/* TikTok (sandbox) */}
      <TikTokSetup settings={settings} onReload={load} />

      {/* A/B testing */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100" data-testid="dist-ab-panel">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-habita" />
          <h3 className="font-bold text-habita-dark">A/B testing del copy</h3>
          {ab?.winner && (
            <span className="ml-auto text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center gap-1" data-testid="dist-ab-winner">
              <Trophy className="w-3.5 h-3.5" /> Gana variante {ab.winner}
            </span>
          )}
        </div>
        {(!ab?.variants || ab.variants.length === 0) ? (
          <p className="text-sm text-gray-500" data-testid="dist-ab-empty">Aún no hay datos A/B. Publica con variantes A/B y los clics del enlace corto <code className="bg-gray-100 px-1 rounded">/p/&#123;id&#125;</code> se contarán aquí.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ab.variants.map((v) => (
              <div key={v.variant} className={`rounded-xl p-4 border ${ab.winner === v.variant ? 'border-amber-300 bg-amber-50' : 'border-gray-100'}`} data-testid={`dist-ab-variant-${v.variant}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-habita-dark">Variante {v.variant}</span>
                  <span className="text-xs text-gray-500">CTR {v.ctr}%</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                  <div><p className="text-lg font-bold text-habita-dark">{v.posts}</p><p className="text-[11px] text-gray-500">Publicaciones</p></div>
                  <div><p className="text-lg font-bold text-habita-dark">{v.impressions}</p><p className="text-[11px] text-gray-500">Impresiones</p></div>
                  <div><p className="text-lg font-bold text-habita-dark">{v.clicks}</p><p className="text-[11px] text-gray-500">Clics</p></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filtros + logs */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <h3 className="font-bold text-habita-dark mr-2">Historial de publicaciones</h3>
          <select data-testid="dist-filter-channel" value={filter.channel} onChange={(e) => setFilter((f) => ({ ...f, channel: e.target.value }))} className="h-9 px-2 rounded-lg border border-gray-200 text-sm">
            <option value="">Todos los canales</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          <select data-testid="dist-filter-status" value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} className="h-9 px-2 rounded-lg border border-gray-200 text-sm">
            <option value="">Todos los estados</option>
            <option value="success">Publicado</option>
            <option value="simulated">Simulado</option>
            <option value="failed">Falló</option>
          </select>
          <select data-testid="dist-filter-trigger" value={filter.trigger} onChange={(e) => setFilter((f) => ({ ...f, trigger: e.target.value }))} className="h-9 px-2 rounded-lg border border-gray-200 text-sm">
            <option value="">Todos los orígenes</option>
            <option value="approved">Al aprobar</option>
            <option value="manual">Manual</option>
            <option value="retry">Reintento</option>
          </select>
        </div>

        {logs.length === 0 ? (
          <div className="py-10 text-center text-gray-500 text-sm" data-testid="dist-logs-empty">Aún no hay publicaciones. Aprueba un inmueble para generar registros automáticos.</div>
        ) : (
          <div className="space-y-2" data-testid="dist-logs-list">
            {logs.map((l) => {
              const c = CHANNEL_META[l.channel] || { label: l.channel, Icon: Send, color: 'text-gray-500' };
              const s = STATUS_META[l.status] || { label: l.status, Icon: Send, cls: 'bg-gray-100 text-gray-600' };
              return (
                <div key={l.id} className="border border-gray-100 rounded-xl p-3 flex items-start gap-3" data-testid={`dist-log-${l.id}`}>
                  <c.Icon className={`w-5 h-5 mt-0.5 shrink-0 ${c.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-habita-dark truncate max-w-[220px]">{l.property_title || l.property_id}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1 ${s.cls}`}><s.Icon className="w-3 h-3" />{s.label}</span>
                      {l.trigger === 'retry' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold">reintento</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2 whitespace-pre-line">{l.copy}</p>
                    {l.error && <p className="text-xs text-red-600 mt-1">⚠ {l.error}</p>}
                  </div>
                  {l.status === 'failed' && (
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button data-testid={`dist-diagnose-${l.id}`} onClick={() => setDiagLog(l)} className="px-2 h-8 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 text-xs font-semibold flex items-center gap-1">
                        <Stethoscope className="w-3.5 h-3.5" /> Ver diagnóstico
                      </button>
                      <button data-testid={`dist-retry-${l.id}`} onClick={() => retry(l.id)} className="px-2 h-8 rounded-lg border border-gray-300 hover:bg-habita-cream text-xs font-semibold flex items-center gap-1">
                        <RotateCcw className="w-3.5 h-3.5" /> Reintentar
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {diagLog && <DistributionDiagnosticModal log={diagLog} onClose={() => setDiagLog(null)} />}
    </div>
  );
}
